import { Calendar, Package, Ship, User } from 'lucide-react'
import React from 'react'

import { useDevanTaskActions } from '../../customs/stores/useDevanTaskStore'

import { customsV2Service } from '@/services'
import { MasterBillSchema, MasterBillStatus } from '@/services/CustomsV2Service'
import { useUIStore } from '@/stores/useUIStore'

interface DevanTaskCardProps {
  masterBill: MasterBillSchema
}

const DevanTaskCard: React.FC<DevanTaskCardProps> = ({ masterBill }) => {
  const { showError, showSuccess } = useUIStore()
  const { refresh } = useDevanTaskActions()
  // 计算统计数据
  const hblCount = masterBill.houseBills?.length || 0
  const totalMarks =
    masterBill.houseBills?.reduce((total, hbl) => {
      return total + (hbl.cargoMarks?.length || 0)
    }, 0) || 0
  const totalPackages = masterBill.totalPackages || 0

  const isAwaiting = masterBill.status === MasterBillStatus.AWAITING_DEVAN

  const handleStartDevan = async () => {
    if (isAwaiting) {
      const { success, message } =
        await customsV2Service.updateMasterBillStatus(
          masterBill.id,
          MasterBillStatus.DEVANNING
        )
      if (success) {
        showSuccess("拆柜任务确认完成, 请开始【扫描入库】")
        refresh()
      } else {
        showError(message)
      }
    }
  }

  return (
    <div
      className={`bg-white rounded-lg shadow-md transition-all duration-200 hover:shadow-lg ${
        isAwaiting
          ? 'border-yellow-200 hover:border-yellow-300'
          : 'border-orange-200 hover:border-orange-300'
      }`}
    >
      {/* 卡片头部 */}
      <div
        className={`px-6 py-4 border-b ${
          isAwaiting ? 'bg-yellow-50' : 'bg-orange-50'
        }`}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            {masterBill.masterBlNumber}
          </h3>
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              isAwaiting
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-orange-100 text-orange-800'
            }`}
          >
            {isAwaiting ? '🔄 待拆柜' : '🔧 拆柜中'}
          </span>
        </div>

        {/* 船舶信息 */}
        <div className="mt-2 flex items-center text-sm text-gray-600">
          <Ship className="h-4 w-4 mr-1" />
          <span>{masterBill.vesselName || '未知船舶'}</span>
          {masterBill.voyageNumber && (
            <span className="ml-2">航次: {masterBill.voyageNumber}</span>
          )}
        </div>

        {/* 箱号信息 */}
        {masterBill.containerNumber && (
          <div className="mt-1 flex items-center text-sm text-gray-600">
            <Package className="h-4 w-4 mr-1" />
            <span>箱号: {masterBill.containerNumber}</span>
            {masterBill.containerSize && (
              <span className="ml-2">({masterBill.containerSize})</span>
            )}
          </div>
        )}
      </div>

      {/* 卡片内容 */}
      <div className="px-6 py-4">
        <div className="grid grid-cols-3 gap-4">
          {/* HBL总数 */}
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{hblCount}</div>
            <div className="text-sm text-gray-500">HBL总数</div>
          </div>

          {/* MARK总数 */}
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {totalMarks}
            </div>
            <div className="text-sm text-gray-500">MARK总数</div>
          </div>

          {/* 箱货总数 */}
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">
              {totalPackages}
            </div>
            <div className="text-sm text-gray-500">箱货总数</div>
          </div>
        </div>

        {/* 日期信息 */}
        <div className="mt-4 space-y-2">
          {masterBill.devanDate && (
            <div className="flex items-center text-sm text-gray-600">
              <Calendar className="h-4 w-4 mr-2" />
              <span>拆柜日期: {masterBill.devanDate}</span>
            </div>
          )}
          {masterBill.inboundDate && (
            <div className="flex items-center text-sm text-gray-600">
              <Calendar className="h-4 w-4 mr-2" />
              <span>预计入库: {masterBill.inboundDate}</span>
            </div>
          )}
        </div>
      </div>

      {/* 操作按钮 */}
      <div
        className={`px-6 py-4 border-t bg-gray-50  ${
          isAwaiting ? 'bg-yellow-50' : 'bg-orange-50'
        }`}
      >
        {isAwaiting && (
          <button
            onClick={handleStartDevan}
            className="w-full bg-yellow-500 hover:bg-yellow-600 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200 flex items-center justify-center"
          >
            <User className="h-4 w-4 mr-2" />
            开始拆柜
          </button>
        )}

        {/* {isDevanning && (
          <div className="flex space-x-2">
            <button
              className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200"
              onClick={() =>
                onStatusChange(masterBill.id, MasterBillStatus.MOVED_IN)
              }
            >
              完成拆柜
            </button>
            <button
              className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200"
              onClick={() =>
                onStatusChange(masterBill.id, MasterBillStatus.AWAITING_DEVAN)
              }
            >
              暂停拆柜
            </button>
          </div>
        )} */}
      </div>
    </div>
  )
}

export default DevanTaskCard
